import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { formatZonedTime } from "../../../lib/time";
import { useEventTitle } from "../../calendar-enrichment/useEventTitles";

/**
 * CalendarOverlayBlock — translucent right-lane overlay tile rendered in
 * week + day calendar views (Chunk E4). Pure presentation — no Firestore,
 * no hooks beyond what React needs.
 *
 * Designed to be rendered as the body of a react-big-calendar event whose
 * `eventPropGetter` has been given an inline-style right-lane laneStyle
 * (see `calendar-overlay-layout.js`). The block fills the parent and
 * exposes a deep-link ↗ icon in the top-right that navigates to
 * /availability?event=<id> or ?mark=<id>.
 *
 * Privacy: when the overlay carries a structural identifier triple
 * ({ provider, calendarId, eventId }), the block resolves the title via
 * the calendar-enrichment hook rather than reading it from the cache.
 * Falls back to overlay.title (used by marks/sessions, which carry
 * user-authored or finalized-session titles legitimately).
 *
 * Props:
 *  - overlay: {
 *      id, kind, title, startUtc, endUtc, allDay, deepLinkHref,
 *      sourceSchedulerId?,
 *      provider?, calendarId?, eventId?,  // structural identifier for enrichment
 *    }
 *  - displayTimeZone?: string
 *  - testId?: string — sets data-testid on the wrapper
 */
export function CalendarOverlayBlock({ overlay, displayTimeZone, testId }) {
  const enrichmentReq =
    overlay?.provider && overlay?.calendarId && overlay?.eventId
      ? { provider: overlay.provider, calendarId: overlay.calendarId, eventId: overlay.eventId }
      : null;
  const enriched = useEventTitle(enrichmentReq);
  if (!overlay) return null;
  const resolvedTitle =
    enriched?.title || overlay.title || "";
  const startDate = overlay.startUtc ? new Date(overlay.startUtc) : null;
  const endDate = overlay.endUtc ? new Date(overlay.endUtc) : null;
  const startLabel = startDate
    ? formatZonedTime(startDate, displayTimeZone, "h:mm a", { showTimeZone: false })
    : "";
  const endLabel = endDate
    ? formatZonedTime(endDate, displayTimeZone, "h:mm a", { showTimeZone: false })
    : "";
  const timeLabel = overlay.allDay
    ? "All day"
    : startLabel && endLabel
      ? `${startLabel}–${endLabel}`
      : startLabel || "";

  const variantClass = `scheduler-overlay-block--${overlay.kind || "conflict"}`;
  // Layout modifier: timed events ride a position:absolute .rbc-event
  // (right-lane laneStyle), so the block uses absolute+inset:0 to fill it.
  // All-day events ride a position:static .rbc-event inside .rbc-row-segment,
  // so absolute would escape to .rbc-allday-cell and stretch across the row;
  // we use normal flow + 100% sizing instead. (H8)
  const layoutClass = overlay.allDay
    ? "scheduler-overlay-block--allday-layout"
    : "scheduler-overlay-block--timed-layout";

  return (
    <div
      className={`scheduler-overlay-block ${variantClass} ${layoutClass}`}
      data-testid={testId || `overlay-${overlay.id}`}
      data-overlay-kind={overlay.kind}
    >
      <div className="flex items-start justify-between gap-1">
        <div
          className="min-w-0 flex-1 truncate text-[10px] font-semibold leading-tight"
          title={resolvedTitle}
        >
          {resolvedTitle}
        </div>
        <Link
          to={overlay.deepLinkHref || "/availability"}
          // RBC's onSelectEvent fires on mousedown→click; without stopping
          // both phases here, clicking the deep link would also open the
          // poll's vote modal at the underlying time.
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded text-current opacity-80 hover:opacity-100"
          aria-label={
            resolvedTitle
              ? `Open ${resolvedTitle} in availability`
              : "Open in availability"
          }
          data-testid={`${testId || `overlay-${overlay.id}`}-deeplink`}
        >
          <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </div>
      {timeLabel && (
        <div className="text-[9px] leading-tight opacity-90">{timeLabel}</div>
      )}
    </div>
  );
}
